import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Sparkles, Send, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function PrivilegeReminderPanel() {
  const [data, setData] = useState({ count: 0, members: [] });
  const [stats, setStats] = useState({ total: 0, registered: 0, not_registered: 0 });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/privilege/non-registered`, { withCredentials: true }),
        axios.get(`${API}/privilege/stats`, { withCredentials: true }),
        axios.get(`${API}/privilege/reminder-runs?limit=1`, { withCredentials: true }),
      ]);
      setData(r1.data);
      setStats(r2.data);
      setLastRun(r3.data?.runs?.[0] || null);
    } catch (e) {
      toast.error("Impossible de charger la liste Privilège.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const send = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/privilege/send-reminders`, {}, { withCredentials: true });
      const { queued, total_targets, sent, failed = [] } = res.data;
      if (queued) {
        toast.success(`Envoi en cours pour ${total_targets} membre${total_targets > 1 ? 's' : ''}. Les emails arrivent dans la minute.`);
      } else if (sent > 0) {
        toast.success(`${sent} relance${sent > 1 ? 's' : ''} envoyée${sent > 1 ? 's' : ''} avec succès.`);
      }
      if (failed.length) {
        toast.error(`${failed.length} envoi${failed.length > 1 ? 's' : ''} en échec.`);
      }
      setConfirm(false);
      // Reload after a short delay to capture the background-run log
      setTimeout(load, 5000);
    } catch (e) {
      toast.error("Erreur pendant le démarrage de la relance.");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-[#E9DDCC] rounded-2xl">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-[#C99A55]/30 rounded-2xl bg-gradient-to-br from-[#F3EADA] to-[#F5EFE5]" data-testid="privilege-reminder-panel">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-[#C99A55]" strokeWidth={1.8} />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#C99A55] font-semibold">Relance Privilège</span>
            </div>
            <h3 className="font-heading text-lg text-[#493329]">Membres Privilège non encore inscrites</h3>
          </div>
          <Button onClick={load} variant="outline" size="sm" className="rounded-full text-xs h-8 border-[#E9DDCC] text-[#7A6455]">
            Actualiser
          </Button>
        </div>

        {/* KPI line */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl p-3 text-center border border-[#E9DDCC]">
            <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Total Privilège</div>
            <div className="font-heading text-2xl text-[#493329]" data-testid="priv-total">{stats.total}</div>
          </div>
          <div className="bg-white rounded-xl p-3 text-center border border-[#E9DDCC]">
            <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Déjà inscrites</div>
            <div className="font-heading text-2xl text-[#493329]" data-testid="priv-registered">{stats.registered}</div>
          </div>
          <div className="bg-white rounded-xl p-3 text-center border border-[#C99A55]/40">
            <div className="text-[10px] uppercase text-[#C99A55] tracking-wider mb-1">À relancer</div>
            <div className="font-heading text-2xl text-[#C99A55]" data-testid="priv-pending">{stats.not_registered}</div>
          </div>
        </div>

        {/* Member list */}
        {data.members.length === 0 ? (
          <div className="flex items-center gap-2 bg-white rounded-xl p-4 border border-[#E9DDCC] text-sm text-[#7A6455]">
            <CheckCircle2 className="w-4 h-4 text-[#7C9C82]" />
            Toutes les membres Privilège se sont déjà inscrites. Bravo !
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#E9DDCC] max-h-[280px] overflow-y-auto">
            <ul className="divide-y divide-[#EFE4D0] text-sm">
              {data.members.map((m, i) => (
                <li key={i} className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-[#493329] font-medium truncate">{m.name || '—'}</span>
                  <span className="text-[#7A6455] text-xs ml-3 truncate">{m.email}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Last run status */}
        {lastRun && (
          <div className="text-xs text-[#7A6455] flex items-center gap-2 italic">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C99A55]" />
            Dernière relance : {new Date(lastRun.ran_at).toLocaleString('fr-FR')} — {lastRun.sent} envoyés{lastRun.failed?.length ? `, ${lastRun.failed.length} en échec` : ''}.
          </div>
        )}

        {/* CTA */}
        {data.members.length > 0 && (
          <>
            {!confirm ? (
              <Button
                onClick={() => setConfirm(true)}
                className="w-full bg-[#C99A55] hover:bg-[#493329] text-white rounded-xl h-12 transition-all duration-300"
                data-testid="priv-send-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Envoyer la relance aux {data.count} membre{data.count > 1 ? 's' : ''} non inscrite{data.count > 1 ? 's' : ''}
              </Button>
            ) : (
              <div className="bg-[#FFF8E6] border border-[#C99A55]/40 rounded-xl p-4">
                <div className="flex items-start gap-2 mb-3">
                  <AlertCircle className="w-4 h-4 text-[#C99A55] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#493329]">
                    Confirmez-vous l'envoi du mail de relance Privilège à <strong>{data.count}</strong> destinataires ?
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setConfirm(false)}
                    variant="outline"
                    className="flex-1 rounded-xl h-10 border-[#E9DDCC] text-[#7A6455]"
                    disabled={sending}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={send}
                    className="flex-1 bg-[#C99A55] hover:bg-[#493329] text-white rounded-xl h-10"
                    disabled={sending}
                    data-testid="priv-confirm-send"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Oui, envoyer'}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        <p className="text-[11px] text-[#AE9885] italic text-center">
          Les destinataires reçoivent l'email premium « ⭐ Votre accès Privilège -20% vous attend encore ».
        </p>
      </CardContent>
    </Card>
  );
}
